import React, { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import {
  Button, Form, Input, message, Modal, Select, Space, Table, Tag
} from 'antd'
import axios from 'axios'

import { HeaderLayout } from 'src/components/HeaderLayout'
import { getFirebaseAuth } from 'src/firebase/firebase'
import { useAuthen } from 'src/hooks/useAuthen'
import { authApiAxiosWithRefresh } from 'src/utils/customAxios'

import MobileUserList from '../../components/MobileUserList/MobileUserList'

// import { currentUser } from '../account/Account'
import './ManageUsers.scss'

const { Search } = Input
const { Option } = Select

export default function ManageUsers() {
  interface User {
    key: string;
    name: string;
    email: string;
    role: string;
    lastActive: string;
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'Admin' ? 'volcano' : 'blue'}>{role}</Tag>
      )
    },
    {
      title: 'Last Active',
      dataIndex: 'lastActive',
      key: 'lastActive'
    }
  ]
  const [data, setData] = useState<any>(null)

  const [filteredData, setFilteredData] = useState(data)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)
  const [isPermissionsModalVisible, setIsPermissionsModalVisible] = useState(false)
  const [isPermissionsMobileModalVisible, setIsPermissionsMobileModalVisible] = useState(false)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [selectedMobileUser, setSelectedMobileUser] = useState<any>(null)
  const [form] = Form.useForm()

  // Need function to get OrganizationID
  // const organizationId = 'A1WdncIfQTG4ROZUp70W'

  const { currentUser } = useAuthen()
  const organizationId = String(currentUser?.uid || '')
  // console.log(currentUser?.uid)

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      const axiosInstance = await authApiAxiosWithRefresh()
      axiosInstance.defaults.headers['Content-Type'] = 'application/json'

      // console.log('Authorization Header:', axiosInstance.defaults.headers.Authorization)
      // console.log(organizationId)
      const response = await axiosInstance.post('org/getUsers', {
        organizationId
      })

      // console.log('Response12:', response.data);

      // Process the response data into the desired format
      const users = response.data.map((user: any, index: number) => ({
        key: `${index + 1}`,
        name: user.fullName || 'N/A',
        email: user.email,
        role: user.role || 'Member',
        lastActive: user.lastActive || 'N/A'
      }))

      console.log('Processed Users:', users)
      setData(users)
      setFilteredData(users)
    } catch (error) {
      console.error('Failed to fetch users:', error)
      message.error('Failed to load users. Please try again later.')
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const addUserToOrganization = async (
    organizationId: string,
    userEmail: string,
    userRole:string
  ) => {
    try {
      const axiosInstance = await authApiAxiosWithRefresh()
      const firebaseAuth = getFirebaseAuth()
      const tokens = await firebaseAuth.currentUser?.getIdToken(true)
      // Log the Axios instance headers to see the Bearer token
      console.log('Authorization Header:', axiosInstance.defaults.headers.Authorization)
      // console.log('Add User Response:', response.data);
      const response = await axiosInstance.post('/org/addUser', {
        organizationId,
        userEmail,
        userRole
      })

      // Log the response data
      console.log('Add User Response:', response.data)
      return response.data
    } catch (error) {
      console.error('Error adding user to organization:', error)
      throw error
    }
  }

  const removeUserFromOrganization = async (organizationId: string, userEmail: string) => {
    try {
      const axiosInstance = await authApiAxiosWithRefresh()
      const response = await axiosInstance.post('/org/removeUser', {
        organizationId,
        userEmail
      })
      console.log('Remove User Response:', response.data)
      return response.data
    } catch (error) {
      console.error('Error removing user from organization:', error)
      throw error
    }
  }

  // Search functionality
  const onSearch = (value: string) => {
    const searchValue = value.toLowerCase()
    const filtered = data.filter(
      (user: User) => user.name.toLowerCase().includes(searchValue)
        || user.role.toLowerCase().includes(searchValue)
    )
    setFilteredData(filtered)
  }

  // Row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[], selectedRows: any[]) => {
      setSelectedRowKeys(selectedKeys)
      setSelectedUser(selectedRows[0] || null)
    }
  }

  // Show modals
  const showCreateModal = () => {
    setIsCreateModalVisible(true)
  }

  const showPermissionsModal = () => {
    setIsPermissionsModalVisible(true)
  }

  const showPermissionsMobileModal = () => {
    setIsPermissionsMobileModalVisible(true)
  }

  const handlePermissionsSubmit = async (
    userEmail: string,
    organizationId: string,
    userRole: string
  ) => {
    if (!userEmail || !userRole) {
      console.log(userEmail, userRole)
      message.error('Missing required information. Please try again.')
      return
    }

    try {
      await addUserToOrganization(organizationId, userEmail, userRole)
      message.success(`Role updated to ${userRole} for ${userEmail}`)
      setIsPermissionsModalVisible(false)
      setData((prevData: User[]) => prevData.map((user) => (user.email === userEmail ? { ...user, role: userRole } : user)))
      setFilteredData((prevData: User[]) => prevData.map((user) => (user.email === userEmail ? { ...user, role: userRole } : user)))
    } catch (error) {
      message.error('Failed to update role. Please try again.')
      console.error('Error updating user role:', error)
    }
  }

  const handleCancel = () => {
    setIsCreateModalVisible(false)
    setIsPermissionsModalVisible(false)
    setIsPermissionsMobileModalVisible(false)
    form.resetFields()
  }

  // handleadduser
  const handleAddUser = async (user: User) => {
    try {
      await addUserToOrganization(organizationId, user.email, user.role)
      fetchUsers()

      message.success('User added successfully!')
    } catch (error) {
      console.error('Error adding user:', error)
      message.error('Failed to add user. Email is not registered with Pacer')
    }
  }

  // const handleRemoveUser = async (userEmails: string[]) => {
  //   try {
  //     // Show confirmation modal
  //     Modal.confirm({
  //       title: 'Are you sure?',
  //       content: `Do you really want to remove the following user(s)?\n\n${userEmails.join(', ')}`,
  //       okText: 'Yes',
  //       cancelText: 'No',
  //       onOk: async () => {
  //         // Remove each user asynchronously
  //         for (const email of userEmails) {
  //           await removeUserFromOrganization(organizationId, email)
  //         }
  //         message.success('User(s) removed successfully!')

  //         // Refresh data after removal
  //         setData((prevData: User[]) => prevData.filter((user: User) => !userEmails.includes(user.email)))
  //         setFilteredData((prevData: User[]) => prevData.filter((user: User) => !userEmails.includes(user.email)))
  //         setSelectedRowKeys([])
  //       }
  //     })
  //   } catch (error) {
  //     message.error('Failed to remove user(s). Please try again.')
  //     console.error('Error in user removal:', error)
  //   }
  // }

  const handleRemoveUser = async (userEmails: string[]) => {
    try {
      // Show confirmation modal
      Modal.confirm({
        title: 'Are you sure?',
        content: `Do you really want to remove the following user(s)?\n\n${userEmails.join(', ')}`,
        okText: 'Yes',
        cancelText: 'No',
        onOk: async () => {
          try {
            // Remove all users concurrently
            await Promise.all(
              userEmails.map((email) => removeUserFromOrganization(organizationId, email))
            )

            message.success('User(s) removed successfully!')

            // Refresh data after removal
            setData((prevData: User[]) => prevData.filter((user: User) => !userEmails.includes(user.email)))
            setFilteredData((prevData: User[]) => prevData.filter((user: User) => !userEmails.includes(user.email)))
            setSelectedRowKeys([])
          } catch (error) {
            message.error('Failed to remove some user(s). Please try again.')
            console.error('Error in user removal:', error)
          }
        }
      })
    } catch (error) {
      message.error('Failed to remove user(s). Please try again.')
      console.error('Error in user removal:', error)
    }
  }

  // Inside the ManageUsers component
  const isMobile = useMediaQuery({ maxWidth: 845 })

  return (
    <>
      <HeaderLayout
        hasGoBack
        title="Manage People"
        subtitle="Manage permissions for users"
      />
      <div className="manage-users-container">
        <div className="manage-users-header">
          <Space align="center" size="large">
            <Search
              placeholder="Search users"
              onSearch={onSearch}
              className="search-bar"
              allowClear
              enterButton
            />
            {!isMobile && (
              <>
                <Button
                  type="default"
                  onClick={showPermissionsModal}
                  disabled={selectedRowKeys.length !== 1}
                >
                  Manage User Permissions
                </Button>
                <Button
                  type="primary"
                  danger
                  onClick={() => handleRemoveUser(
                    selectedRowKeys
                      .map((key) => data.find((user: User) => user.key === key)?.email)
                      .filter((email): email is string => email !== undefined)
                  )}
                  disabled={selectedRowKeys.length === 0}
                >
                  Remove User
                </Button>
              </>
            )}
            <Button type="primary" onClick={showCreateModal}>
              Create User
            </Button>
          </Space>
        </div>
        {isMobile ? (
          <MobileUserList
            users={filteredData}
            onManagePermissions={(user) => {
              setSelectedMobileUser(user)
              showPermissionsMobileModal()
            }}
            onRemoveUser={(user) => {
              Modal.confirm({
                title: `Remove ${user.name}?`,
                content: 'This action cannot be undone.',
                okText: 'Yes',
                cancelText: 'No',
                onOk: () => handleRemoveUser([user.email])

              })
            }}
          />
        ) : (
          <Table
            // rowSelection={rowSelection}
            rowSelection={{
              selectedRowKeys,
              onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys)
            }}
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 5 }}
            rowClassName={(record) => (selectedRowKeys.includes(record.key) ? 'ant-table-row-selected' : '')}
            onRow={(record) => ({
              onClick: () => {
              // Toggle selection when a row is clicked
                const isSelected = selectedRowKeys.includes(record.key)
                const newSelectedKeys = isSelected
                  ? selectedRowKeys.filter((key) => key !== record.key)
                  : [...selectedRowKeys, record.key]

                setSelectedRowKeys(newSelectedKeys)
              }
            })}
          />
        )}
      </div>

      {/* Create User Modal */}
      <Modal
        title="Create User"
        visible={isCreateModalVisible}
        onCancel={handleCancel}
        footer={null}
        className="manage-users-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => {
            const newUser: User = {
              key: `${data.length + 1}`,
              name: values.name,
              email: values.email,
              role: values.role,
              lastActive: new Date().toLocaleString('en-US', {
                day: '2-digit',
                month: 'short'
              })
            }
            handleAddUser(newUser) // Call handleAddUser here
            setIsCreateModalVisible(false)
            form.resetFields()
          }}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter the email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="Enter user's email" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select a role">
              <Option value="Admin">Admin</Option>
              <Option value="Member">Member</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Create User
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Manage Permissions Modal */}
      <Modal
        title="Manage User Permissions"
        open={isPermissionsModalVisible}
        onCancel={handleCancel}
        footer={null}
        className="manage-users-modal"
      >
        <Form
          layout="vertical"
          onFinish={(values) => {
            const selectedEmail = data
              .find((user: User) => user.key === selectedRowKeys[0])?.email

            handlePermissionsSubmit(
              selectedEmail,
              organizationId,
              values.role
            )
          }}
          initialValues={{ role: selectedUser?.role }}
        >
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select a role">
              <Option value="Admin">Admin</Option>
              <Option value="Member">Member</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Update Role
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Manage Permissions Mobile Modal */}
      <Modal
        title="Manage User Permissions"
        open={isPermissionsMobileModalVisible}
        onCancel={handleCancel}
        footer={null}
        className="manage-users-modal"
      >
        <Form
          layout="vertical"
          onFinish={(values) => {
            const selectedEmail = selectedMobileUser.email
            console.log(selectedMobileUser.email)

            handlePermissionsSubmit(
              selectedEmail,
              organizationId,
              values.role
            )
          }}
          initialValues={{ role: selectedUser?.role }}
        >
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select a role">
              <Option value="Admin">Admin</Option>
              <Option value="Member">Member</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Update Role
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>

  )
}
